import styled, { keyframes } from 'styled-components'

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 999;
  animation: ${fadeIn} 0.2s ease-out;
`

const Panel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 450px;
  max-width: 90vw;
  height: 100vh;
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.3s ease-out;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
`

const PanelTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.text};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 0;
  line-height: 1;
  transition: color ${({ theme }) => theme.transitions.normal};

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.md};
`

const OpportunityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const OpportunityCard = styled.div<{ $clickable: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: all ${({ theme }) => theme.transitions.normal};

  ${({ $clickable, theme }) => $clickable && `
  &:hover {
    border-color: ${theme.colors.primary};
    background: white;
    transform: translateX(4px);
  }
  `}
`

const OpportunityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const OpportunityName = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  flex: 1;
`

interface StatusBadgeProps {
    color: string
}

const StatusBadge = styled.span<StatusBadgeProps>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  background: ${({ color }) => color};
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
`

const OpportunityMeta = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textMuted};
`

export interface Opportunity {
    id: string
    name: string
    status: string
    statusColor: string
    sector?: string
    gestor?: string
    amount?: number
    /** A percentage, or the offer's probability label ('alta' | 'media' | 'baja'). */
    probability?: number | string
}

interface OpportunitiesPanelProps {
    isOpen: boolean
    title: string
    opportunities: Opportunity[]
    description?: string
    onClose: () => void
    onOpportunityClick?: (opportunity: Opportunity) => void
}

const formatAmount = (amount?: number): string => {
    if (!amount) return '-'
    if (amount >= 1000000) {
        return `${(amount / 1000000).toFixed(1)}M€`
    }
    if (amount >= 1000) {
        return `${(amount / 1000).toFixed(0)}K€`
    }
    return `${amount}€`
}

// oferta.probabilidad is a label ('alta'), not a number: it rendered as 'alta%'.
const formatProbability = (probability: number | string): string => {
    if (typeof probability === 'number') return `${probability}%`
    return probability.charAt(0).toUpperCase() + probability.slice(1)
}

export const OpportunitiesPanel: React.FC<OpportunitiesPanelProps> = ({
    isOpen,
    title,
    opportunities,
    description,
    onClose,
    onOpportunityClick,
}) => {
    if (!isOpen) return null

    return (
        <>
            <Overlay onClick={onClose} />
            <Panel>
                <PanelHeader>
                    <PanelTitle>{title}</PanelTitle>
                    <CloseButton onClick={onClose}>&times;</CloseButton>
                </PanelHeader>
                <PanelContent>
                    {description && (
                        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
                            {description}
                        </p>
                    )}
                    {opportunities.length === 0 ? (
                        <EmptyState>
                            <p>No hay oportunidades en esta categoría</p>
                        </EmptyState>
                    ) : (
                        <OpportunityList>
                            {opportunities.map((opp) => (
                                <OpportunityCard
                                    key={opp.id}
                                    $clickable={!!onOpportunityClick}
                                    onClick={() => onOpportunityClick?.(opp)}
                                >
                                    <OpportunityHeader>
                                        <OpportunityName>{opp.name}</OpportunityName>
                                        <StatusBadge color={opp.statusColor}>
                                            {opp.status}
                                        </StatusBadge>
                                    </OpportunityHeader>
                                    <OpportunityMeta>
                                        {opp.sector && (
                                            <MetaItem>📂 {opp.sector}</MetaItem>
                                        )}
                                        {opp.gestor && (
                                            <MetaItem>👤 {opp.gestor}</MetaItem>
                                        )}
                                        {!!opp.amount && (
                                            <MetaItem>💰 {formatAmount(opp.amount)}</MetaItem>
                                        )}
                                        {opp.probability !== undefined && opp.probability !== null && opp.probability !== '' && (
                                            <MetaItem>📊 {formatProbability(opp.probability)}</MetaItem>
                                        )}
                                    </OpportunityMeta>
                                </OpportunityCard>
                            ))}
                        </OpportunityList>
                    )}
                </PanelContent>
            </Panel>
        </>
    )
}
